// src/components/CommunicationLogList.jsx
// ============================================================================
// Lightweight communication log display for email draft audit trail.
// Shows a chronological list of email drafts opened from the app.
// NOT an inbox — only tracks actions taken within TimeTracker Pro.
//
// Supports filtering by:
//   - Event type group (invoice, reminder, stage)
//   - Invoice ID (from payload.invoiceId)
//   - Stage name (from payload.stageName)
// ============================================================================
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Mail, Bell, FileText, Clock, ChevronDown, ChevronUp, RefreshCw, Filter } from 'lucide-react';

// ── Event type display config ────────────────────────────────────────────────
const EVENT_CONFIG = {
  stage_initiated:          { label: 'Stage Initiation',     icon: FileText, color: 'var(--brio-teal, #689581)', group: 'stage' },
  stage_approved:           { label: 'Stage Approved',       icon: FileText, color: 'var(--brio-teal, #689581)', group: 'stage' },
  stage_closure:            { label: 'Stage Closure',        icon: FileText, color: 'var(--brio-teal, #689581)', group: 'stage' },
  timeline_change:          { label: 'Timeline Change',      icon: Clock,    color: 'var(--brio-gold, #9F902A)', group: 'stage' },
  invoice_sent:             { label: 'Tax Invoice',          icon: Mail,     color: 'var(--brio-gold, #9F902A)', group: 'invoice' },
  proforma_sent:            { label: 'Proforma Invoice',     icon: Mail,     color: 'var(--brio-gold, #9F902A)', group: 'invoice' },
  reminder_friendly:        { label: 'Friendly Reminder',    icon: Bell,     color: 'var(--brio-yellow, #ECD288)', group: 'reminder' },
  reminder_due_today:       { label: 'Due Today Reminder',   icon: Bell,     color: 'var(--brio-orange, #DB6B5D)', group: 'reminder' },
  reminder_overdue:         { label: 'Overdue Reminder',     icon: Bell,     color: 'var(--brio-red, #934948)', group: 'reminder' },
  reminder_final:           { label: 'Final Reminder',       icon: Bell,     color: 'var(--brio-red, #934948)', group: 'reminder' },
  monthly_accountant_report:{ label: 'Accountant Report',    icon: FileText, color: 'var(--brio-grey, #666)', group: 'finance' },
};

const FILTER_GROUPS = [
  { key: 'all', label: 'All' },
  { key: 'invoice', label: 'Invoices' },
  { key: 'reminder', label: 'Reminders' },
  { key: 'stage', label: 'Stages' },
];

function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${min}`;
}

function recipientsSummary(payload) {
  const to = payload?.to || [];
  if (to.length === 0) return 'No recipients';
  if (to.length === 1) return to[0];
  return `${to[0]} +${to.length - 1} more`;
}

/**
 * CommunicationLogList
 *
 * Displays a chronological list of communication events (email drafts opened).
 *
 * @param {object} props
 * @param {Function} props.fetchLogs   - Async function that returns log entries
 * @param {string}   [props.emptyText] - Text when no logs exist
 * @param {number}   [props.limit]     - Max entries to show (default 10)
 * @param {boolean}  [props.compact]   - Compact mode for inline embedding
 * @param {number}   [props.filterInvoiceId] - Pre-filter to show only logs for this invoice
 * @param {string}   [props.filterStageName] - Pre-filter to show only logs for this stage
 */
export default function CommunicationLogList({
  fetchLogs,
  emptyText = 'No communication history yet',
  limit = 10,
  compact = false,
  filterInvoiceId = null,
  filterStageName = null,
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [activeGroup, setActiveGroup] = useState('all');
  const [stageFilter, setStageFilter] = useState(filterStageName || '');
  const [showFilters, setShowFilters] = useState(!!(filterInvoiceId || filterStageName));

  const load = useCallback(async () => {
    if (!fetchLogs) return;
    setLoading(true);
    try {
      const data = await fetchLogs();
      setLogs(data || []);
    } catch (_) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [fetchLogs]);

  useEffect(() => { load(); }, [load]);

  // Extract unique stage names for the stage filter dropdown
  const stageNames = useMemo(() => {
    const names = new Set();
    logs.forEach(log => {
      const sn = log.payload?.stageName;
      if (sn) names.add(sn);
    });
    return [...names].sort();
  }, [logs]);

  // Apply filters
  const filteredLogs = useMemo(() => {
    let result = logs;

    // Group filter (invoice, reminder, stage)
    if (activeGroup !== 'all') {
      result = result.filter(log => {
        const eventType = log.payload?.eventType || log.payload?.type || '';
        const config = EVENT_CONFIG[eventType];
        return config?.group === activeGroup;
      });
    }

    // Invoice ID filter (from prop)
    if (filterInvoiceId) {
      const invId = Number(filterInvoiceId);
      result = result.filter(log => {
        const logInvId = log.payload?.invoiceId;
        return logInvId && Number(logInvId) === invId;
      });
    }

    // Stage name filter
    if (stageFilter) {
      result = result.filter(log => {
        return log.payload?.stageName === stageFilter;
      });
    }

    return result;
  }, [logs, activeGroup, filterInvoiceId, stageFilter]);

  const visibleLogs = showAll ? filteredLogs : filteredLogs.slice(0, limit);
  const hasMore = filteredLogs.length > limit;

  if (loading) {
    return (
      <div style={{ padding: compact ? '8px 0' : '16px', fontSize: '12px', color: 'var(--brio-muted, #94a3b8)', textAlign: 'center' }}>
        Loading communication history…
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div style={{
        padding: compact ? '10px 0' : '20px',
        fontSize: '12px',
        color: 'var(--brio-muted, #94a3b8)',
        textAlign: 'center',
        fontStyle: 'italic',
      }}>
        {emptyText}
      </div>
    );
  }

  return (
    <div>
      {/* Header with refresh + filter toggle */}
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--brio-text, #475569)' }}>
            Communication History ({filteredLogs.length}{filteredLogs.length !== logs.length ? ` of ${logs.length}` : ''})
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                background: showFilters ? 'var(--brio-gold-light, #fef9e7)' : 'none',
                border: showFilters ? '1px solid var(--brio-gold, #9F902A)' : 'none',
                cursor: 'pointer', padding: '2px 5px', borderRadius: '3px',
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                fontSize: '10px', color: showFilters ? 'var(--brio-gold, #9F902A)' : 'var(--brio-muted, #94a3b8)',
              }}
              title="Toggle filters"
            >
              <Filter className="w-3 h-3" /> Filter
            </button>
            <button
              onClick={load}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--brio-muted, #94a3b8)', padding: '2px',
                display: 'inline-flex', alignItems: 'center',
              }}
              title="Refresh"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      {showFilters && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px',
          padding: '6px 8px', background: '#f8fafc', borderRadius: '4px',
          fontSize: '10px', flexWrap: 'wrap',
        }}>
          {/* Type group chips */}
          {FILTER_GROUPS.map(g => (
            <button
              key={g.key}
              onClick={() => { setActiveGroup(g.key); setShowAll(false); }}
              style={{
                padding: '2px 8px', fontSize: '10px', borderRadius: '10px',
                border: '1px solid',
                borderColor: activeGroup === g.key ? 'var(--brio-gold, #9F902A)' : '#d1d5db',
                background: activeGroup === g.key ? 'var(--brio-gold-light, #fef9e7)' : 'transparent',
                color: activeGroup === g.key ? 'var(--brio-gold, #9F902A)' : '#64748b',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {g.label}
            </button>
          ))}

          {/* Stage filter dropdown */}
          {stageNames.length > 0 && (
            <>
              <span style={{ color: '#94a3b8' }}>|</span>
              <select
                value={stageFilter}
                onChange={e => { setStageFilter(e.target.value); setShowAll(false); }}
                style={{
                  fontSize: '10px', padding: '2px 6px',
                  border: '1px solid #d1d5db', borderRadius: '4px',
                  fontFamily: 'inherit', background: 'white',
                }}
              >
                <option value="">All stages</option>
                {stageNames.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </>
          )}

          {/* Clear filters */}
          {(activeGroup !== 'all' || stageFilter) && (
            <button
              onClick={() => { setActiveGroup('all'); setStageFilter(''); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '9px', color: 'var(--brio-gold, #9F902A)',
                textDecoration: 'underline', fontFamily: 'inherit',
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Filtered empty state */}
      {filteredLogs.length === 0 && logs.length > 0 && (
        <div style={{
          padding: '12px', fontSize: '11px', color: '#94a3b8',
          textAlign: 'center', fontStyle: 'italic',
        }}>
          No entries match the current filters.
        </div>
      )}

      {/* Log entries */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {visibleLogs.map((log, i) => {
          const payload = log.payload || {};
          const eventType = payload.eventType || payload.type || '';
          const config = EVENT_CONFIG[eventType] || { label: eventType || 'Email', icon: Mail, color: '#94a3b8' };
          const Icon = config.icon;
          const isExpanded = expanded === i;
          const ts = payload.createdAt || log.created_at;

          return (
            <div key={log.id || i}>
              <button
                onClick={() => setExpanded(isExpanded ? null : i)}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 8px',
                  background: isExpanded ? 'var(--brio-gold-light, #fef9e7)' : 'transparent',
                  border: 'none', borderRadius: '4px',
                  cursor: 'pointer', textAlign: 'left',
                  fontSize: '11px', color: 'var(--brio-text, #475569)',
                }}
              >
                <Icon className="w-3 h-3" style={{ color: config.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 500, minWidth: '100px' }}>{config.label}</span>
                <span style={{ color: 'var(--brio-muted, #94a3b8)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {recipientsSummary(payload)}
                </span>
                <span style={{ color: 'var(--brio-muted, #94a3b8)', fontSize: '10px', whiteSpace: 'nowrap' }}>
                  {formatTimestamp(ts)}
                </span>
                <span style={{ color: '#16a34a', fontSize: '9px', fontWeight: 500 }}>Draft opened</span>
                {isExpanded ? <ChevronUp className="w-3 h-3" style={{ color: '#94a3b8' }} /> : <ChevronDown className="w-3 h-3" style={{ color: '#94a3b8' }} />}
              </button>

              {/* Expanded details */}
              {isExpanded && (
                <div style={{
                  padding: '6px 8px 8px 28px',
                  fontSize: '10px', color: 'var(--brio-text, #475569)',
                  background: 'var(--brio-gold-light, #fef9e7)',
                  borderRadius: '0 0 4px 4px',
                  display: 'flex', flexDirection: 'column', gap: '3px',
                }}>
                  {payload.subject && (
                    <div><strong>Subject:</strong> {payload.subject}</div>
                  )}
                  {payload.to && payload.to.length > 0 && (
                    <div><strong>To:</strong> {payload.to.join(', ')}</div>
                  )}
                  {payload.cc && payload.cc.length > 0 && (
                    <div><strong>CC:</strong> {payload.cc.join(', ')}</div>
                  )}
                  {payload.bcc && payload.bcc.length > 0 && (
                    <div><strong>BCC:</strong> {payload.bcc.join(', ')}</div>
                  )}
                  {payload.stageName && (
                    <div><strong>Stage:</strong> {payload.stageName}</div>
                  )}
                  {payload.invoiceId && (
                    <div><strong>Invoice:</strong> #{payload.invoiceId}</div>
                  )}
                  {payload.templateUsed && (
                    <div><strong>Template:</strong> {payload.templateUsed}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Show more / less */}
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          style={{
            display: 'block', width: '100%', marginTop: '4px',
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '10px', color: 'var(--brio-gold, #9F902A)',
            fontWeight: 500, textAlign: 'center', padding: '4px',
          }}
        >
          {showAll ? `Show less` : `Show all ${filteredLogs.length} entries`}
        </button>
      )}
    </div>
  );
}
